import {
  hasUpgradeModalError,
  showToastNotification,
  ToastMessageOptionsType,
} from 'app/toasts/slice';

import {apiQuery} from '../api-query';

import type {
  MutationLifecycleApi,
  QueryFulfilledRejectionReason,
  QueryLifecycleApi,
} from '@reduxjs/toolkit/dist/query/endpointDefinitions';

export function showMessages<A, R>({
  onStart,
  onError,
  onSuccess,
}: {
  onStart?: () =>
    | string
    | null
    | [message: string, options: ToastMessageOptionsType];
  onError?:
    | ((
        error: QueryFulfilledRejectionReason<typeof apiQuery>,
        args: A
      ) => string)
    | {
        fallback: (errorResponse: Record<string, any> | null) => string;
        byStatus: {
          [statusCode: number]: (
            errorResponse: Record<string, any> | null
          ) => string | null | undefined;
        };
        silenceByStatus?: {
          [statusCode: number]: boolean;
        };
      };

  onSuccess?: (
    args: A,
    response: {data: R; meta: Record<string, unknown> | undefined}
  ) => string | null | [message: string, options: ToastMessageOptionsType];
}) {
  return function (
    args: A,
    api:
      | QueryLifecycleApi<A, typeof apiQuery, R, 'sweetAPI'>
      | MutationLifecycleApi<A, typeof apiQuery, R, 'sweetAPI'>
  ): Promise<void> | void {
    const {dispatch, queryFulfilled} = api;

    if (onStart != null) {
      const message = onStart();
      if (message != null) {
        dispatch(
          Array.isArray(message)
            ? showToastNotification(...message)
            : showToastNotification(message)
        );
      }
    }
    queryFulfilled.then(
      (response) => {
        if (onSuccess != null) {
          const message = onSuccess(args, response);
          if (message != null) {
            dispatch(
              Array.isArray(message)
                ? showToastNotification(...message)
                : showToastNotification(message)
            );
          }
        }

        return response;
      },
      (err) => {
        if (onError != null)
          if (typeof onError === 'function') {
            dispatch(
              showToastNotification(onError(err, args), {level: 'error'})
            );
          } else {
            // silence notification when the error contains a global status type code.
            // because those trigger a modal and don't need the toast.
            let silence = hasUpgradeModalError((err?.error as any)?.data);

            if ('error' in err) {
              const {fallback, byStatus, silenceByStatus} = onError;
              const error = err.error as any;
              let message = fallback(error.data);

              if (typeof error?.status == 'number') {
                const status: number = error.status;

                if (status in byStatus) {
                  message = byStatus[status](error.data) ?? message;
                }

                if (silenceByStatus && silenceByStatus[status]) {
                  silence = true;
                }
              }

              if (!silence) {
                dispatch(showToastNotification(message, {level: 'error'}));
              }
            }
          }
      }
    );
  };
}
